import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaHandshake, FaBalanceScale, FaChartLine, FaCalculator, FaUserFriends, FaGraduationCap } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const SocieteCooperative = () => {
  const [setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const faqSection = document.getElementById("faq-section");
      if (faqSection) {
        const rect = faqSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const servicesData = [
    {
      icon: <FaCalculator />,
      title: "Comptabilité, Fiscalité et Gestion Sociale",
      description: "Tenue comptable, établissement des comptes annuels, gestion fiscale et sociale spécifiques aux coopératives.",
      color: "#0d6efd"
    },
    {
      icon: <FaBalanceScale />,
      title: "Audit et Contrôle",
      description: "Audit légal, contractuel et mise en place de procédures de contrôle interne adaptées à votre structure coopérative.",
      color: "#198754"
    },
    {
      icon: <FaChartLine />,
      title: "Conseil et Accompagnement Stratégique",
      description: "Accompagnement à la création, transformation, développement et optimisation de votre société coopérative.",
      color: "#dc3545"
    },
    {
      icon: <FaGraduationCap />,
      title: "Formations",
      description: "Formations sur-mesure pour vos associés-coopérateurs et salariés en matière de comptabilité, gestion et gouvernance coopérative.",
      color: "#6610f2"
    }
  ];

  const serviceDetails = {
    comptabilite: [
      {
        title: "Tenue et révision comptable",
        description: "Gestion rigoureuse de votre comptabilité, en conformité avec les normes comptables et les réglementations spécifiques aux coopératives."
      },
      {
        title: "Établissement des comptes annuels",
        description: "Bilan, compte de résultat, annexe, et production des documents obligatoires. Prise en compte des spécificités (réserves impartageables, ristournes, etc.)."
      },
      {
        title: "Gestion de la fiscalité",
        description: "Maîtrise des régimes fiscaux applicables (impôt sur les sociétés, exonérations, etc.), déclarations fiscales."
      },
      {
        title: "Déclarations fiscales et sociales",
        description: "Prise en charge de l'ensemble de vos déclarations."
      },
      {
        title: "Gestion de la paie et du social",
        description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail. Accompagnement spécifique pour les associés-coopérateurs (rémunération, statut, etc.)."
      },
      {
        title: "Assistance en cas de contrôle",
        description: "Accompagnement complet et défense de vos intérêts lors de contrôles fiscaux ou sociaux."
      },
      {
        title: "Comptabilité analytique",
        description: "Mise en place d'outils de suivi des coûts par activité, par projet, pour une meilleure maîtrise de votre rentabilité."
      }
    ],
    audit: [
      {
        title: "Audit légal (commissariat aux comptes)",
        description: "Certification de vos comptes annuels (si votre structure est soumise à cette obligation)."
      },
      {
        title: "Audit contractuel",
        description: "Audit de vos procédures internes, de votre gestion financière."
      },
      {
        title: "Mise en place et optimisation du contrôle interne",
        description: "Renforcement de la maîtrise de vos risques."
      }
    ],
    conseil: [
      {
        title: "Accompagnement à la création et transformation",
        description: "Étude de faisabilité, choix du statut, business plan, formalités juridiques, recherche de financements pour votre société coopérative."
      },
      {
        title: "Conseil en gouvernance",
        description: "Optimisation du fonctionnement de vos instances (conseil d'administration, assemblée générale, etc.), accompagnement des associés-coopérateurs."
      },
      {
        title: "Conseil en gestion financière",
        description: "Analyse de la situation financière, optimisation de la trésorerie, recherche de financements (prêts bancaires, aides spécifiques aux coopératives, etc.)."
      },
      {
        title: "Conseil en gestion des ressources humaines",
        description: "Optimisation de l'organisation, gestion des compétences, recrutement, accompagnement des transitions (départ d'associés, etc.)."
      },
      {
        title: "Accompagnement des projets de développement",
        description: "Croissance interne, diversification, rapprochement avec d'autres structures."
      },
      {
        title: "Conseil juridique",
        description: "Assistance pour les statuts, règlements intérieurs, contrats (en partenariat avec des avocats spécialisés, si applicable)."
      },
      {
        title: "Accompagnement à la transformation digitale",
        description: "Mise en place d'outils numériques de gestion, de communication, et de collaboration."
      },
      {
        title: "Conseil en matière de réglementation",
        description: "Accompagnement sur les obligations spécifiques aux Sociétés Coopératives."
      }
    ],
    formation: [
      {
        title: "Formations sur-mesure",
        description: "Adaptées aux besoins de vos associés-coopérateurs et de vos salariés (comptabilité, gestion, gouvernance coopérative, etc.)."
      },
      {
        title: "Formations inter- ou intra-structures",
        description: "Pour l'ensemble de vos collaborateurs et partenaires."
      }
    ]
  };

  return (
    <>
      <Header />

      <section className="service-banner d-flex align-items-center">
        <div className="container-fluid px-4 px-md-5 text-white">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour les Sociétés Coopératives</h1>
              <p className="fs-5 mb-4">Soutenir la Performance et les Valeurs de Votre Coopérative</p>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-white text-decoration-none">
                      Accueil
                    </Link>{" "}
                    /{" "}
                    <Link to="/services" className="text-white text-decoration-none">
                      Services
                    </Link>
                  </li>
                  <li className="breadcrumb-item active text-white" aria-current="page">
                    Sociétés Coopératives
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row">
            <div className="col-12">
              <h2 className="fw-bold custom-title text-center mb-5">
                CINEF-EC comprend les enjeux spécifiques des sociétés coopératives
              </h2>
              <p className="text-muted text-center fs-5 mb-5">
                Vous avez choisi le modèle coopératif pour ses valeurs de démocratie, de partage et de solidarité ? 
                CINEF-EC comprend les enjeux et les spécificités de votre entreprise. Nous vous proposons un accompagnement 
                comptable, financier, juridique et stratégique complet, pour vous permettre de vous concentrer sur le 
                développement de votre activité et la réalisation de vos projets collectifs.
              </p>
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-lg-12">
              <h3 className="fw-bold custom-title mb-4">Une expertise dédiée aux sociétés coopératives</h3>
              <p className="mb-4">
                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des sociétés coopératives. 
                Nous connaissons parfaitement les spécificités comptables, fiscales et juridiques liées à ce modèle d'entreprise.
              </p>
              <p className="mb-4">
                Notre objectif est double : vous accompagner dans vos obligations légales et réglementaires 
                tout en vous aidant à développer votre activité dans le respect des valeurs coopératives.
              </p>
              <div className="d-flex mt-4">
                <div className="d-flex align-items-center me-4">
                  <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <FaUserFriends className="text-white fs-4" />
                  </div>
                  <span className="ms-2 fw-bold">SCOP</span>
                </div>
                <div className="d-flex align-items-center me-4">
                  <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <FaHandshake className="text-white fs-4" />
                  </div>
                  <span className="ms-2 fw-bold">SCIC</span>
                </div>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                    <FaChartLine className="text-white fs-4" />
                  </div>
                  <span className="ms-2 fw-bold">Coopératives agricoles</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section background="#f8f9fa">
        <div className="container-fluid px-4 px-md-5">
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h2 className="fw-bold mb-1">Nos services pour les sociétés coopératives</h2>
              <div className="d-inline-block mb-4">
                <div className="custom-underline mx-auto"></div>
              </div>
              <p className="text-muted fs-5 mb-5">Une gamme complète de services adaptés aux spécificités des coopératives</p>
            </div>
          </div>

          <div className="row">
            {servicesData.map((service, index) => (
              <div className="col-md-6 col-lg-3 mb-4" key={index}>
                <ServiceCard delay={`${index * 0.1}s`}>
                  <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row mb-5">
            <div className="col-12">
              <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Fiscalité et Gestion Sociale</h3>
            </div>
          </div>

          <div className="row mb-5">
            {serviceDetails.comptabilite.map((item, index) => (
              <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                <ServiceListItem delay={`${index * 0.1}s`}>
                  <ServiceListIcon>
                    <i className="fas fa-check-circle fa-lg"></i>
                  </ServiceListIcon>
                  <ServiceListContent>
                    <ServiceListTitle>{item.title}</ServiceListTitle>
                    <ServiceListDescription>{item.description}</ServiceListDescription>
                  </ServiceListContent>
                </ServiceListItem>
              </div>
            ))}
          </div>

          <div className="row mb-5">
            <div className="col-12">
              <h3 className="fw-bold custom-title mb-4">2. Audit et Contrôle</h3>
            </div>
          </div>

          <div className="row mb-5">
            {serviceDetails.audit.map((item, index) => (
              <div className="col-md-6 mb-4" key={`audit-${index}`}>
                <ServiceListItem delay={`${index * 0.1}s`}>
                  <ServiceListIcon>
                    <i className="fas fa-check-circle fa-lg"></i>
                  </ServiceListIcon>
                  <ServiceListContent>
                    <ServiceListTitle>{item.title}</ServiceListTitle>
                    <ServiceListDescription>{item.description}</ServiceListDescription>
                  </ServiceListContent>
                </ServiceListItem>
              </div>
            ))}
          </div>

          <div className="row mb-5">
            <div className="col-12">
              <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
            </div>
          </div>

          <div className="row mb-5">
            {serviceDetails.conseil.map((item, index) => (
              <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                <ServiceListItem delay={`${index * 0.1}s`}>
                  <ServiceListIcon>
                    <i className="fas fa-check-circle fa-lg"></i>
                  </ServiceListIcon>
                  <ServiceListContent>
                    <ServiceListTitle>{item.title}</ServiceListTitle>
                    <ServiceListDescription>{item.description}</ServiceListDescription>
                  </ServiceListContent>
                </ServiceListItem>
              </div>
            ))}
          </div>

          <div className="row mb-5">
            <div className="col-12">
              <h3 className="fw-bold custom-title mb-4">4. Formations</h3>
            </div>
          </div>

          <div className="row mb-5">
            {serviceDetails.formation.map((item, index) => (
              <div className="col-md-6 mb-4" key={`formation-${index}`}>
                <ServiceListItem delay={`${index * 0.1}s`}>
                  <ServiceListIcon>
                    <i className="fas fa-check-circle fa-lg"></i>
                  </ServiceListIcon>
                  <ServiceListContent>
                    <ServiceListTitle>{item.title}</ServiceListTitle>
                    <ServiceListDescription>{item.description}</ServiceListDescription>
                  </ServiceListContent>
                </ServiceListItem>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="container-fluid px-4 px-md-5">
          <div className="row justify-content-center mb-5">
            <div className="col-12 col-md-10 col-lg-8">
              <div className="call-to-action p-5 rounded-3 text-center">
                <h3 className="fw-bold text-white mb-3">Vous recherchez un partenaire expert pour accompagner le développement de votre coopérative ?</h3>
                <p className="text-white fs-5 mb-4">
                  Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée. 
                  Nous évaluerons ensemble vos besoins et vous proposerons des solutions adaptées à votre projet et à vos valeurs.
                </p>
                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/cooperative-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
    </>
  );
};

export default SocieteCooperative;